import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { AppUser } from '../../../../models/app-user.model';
import { AppUserService } from '../../../../services/app-user.service';

@Component({
  selector: 'app-users-page',
  templateUrl: './users-page.component.html',
  styleUrl: './users-page.component.scss'
})
export class UsersPageComponent {

  @ViewChild('dt') dt!: Table;

  public first: number = 0;
  public rows: number = 25;

  /* Lo que el usuario está escribiendo. NO dispara peticiones por tecleo: la
     búsqueda es del servidor y se manda al enviar el formulario. */
  public searchTerm: string = '';

  /* El término que realmente viajó al backend. Es el que decide si el estado
     vacío dice «no hay usuarios» o «no hay resultados para X», y el que se
     devuelve al input al volver de una búsqueda. */
  public appliedSearch: string = '';

  /* Filas fantasma: solo le dan cuerpo a la tabla mientras loading es true, con
     el mismo alto que las filas reales para que nada salte al llegar los datos.
     Por eso NO se usa [loading] de p-table: ese pinta un overlay que bloquea. */
  public skeletonRows: null[] = [
    null, null, null, null, null,
    null, null, null, null, null
  ];

  /* Única región viva de la pantalla. Vive SIEMPRE en el DOM: un aria-live que
     aparece junto con su texto no se anuncia. */
  public liveMessage: string = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    public appUserService: AppUserService,
    private router: Router
  ) { }

  /* El valor de la tabla: esqueletos mientras carga, usuarios cuando llegan. */
  get tableValue(): (AppUser | null)[] {
    return this.appUserService.loading
      ? this.skeletonRows
      : this.appUserService.users;
  }

  /* Vacío por búsqueda y vacío por catálogo son dos pantallas distintas: la
     primera ofrece limpiar el término, la segunda no tiene nada que ofrecer. */
  get isSearchEmpty(): boolean {
    return this.appliedSearch !== '';
  }

  /*
   ██████╗██╗     ███████╗ █████╗ ██████╗  ███████╗███████╗ █████╗ ██████╗  ██████╗██╗  ██╗
  ██╔════╝██║     ██╔════╝██╔══██╗██╔══██╗ ██╔════╝██╔════╝██╔══██╗██╔══██╗██╔════╝██║  ██║
  ██║     ██║     █████╗  ███████║██████╔╝ ███████╗█████╗  ███████║██████╔╝██║     ███████║
  ██║     ██║     ██╔══╝  ██╔══██║██╔══██╗ ╚════██║██╔══╝  ██╔══██║██╔══██╗██║     ██╔══██║
  ╚██████╗███████╗███████╗██║  ██║██║  ██║ ███████║███████╗██║  ██║██║  ██║╚██████╗██║  ██║
   ╚═════╝╚══════╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚══════╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝

  */
  // region clearSearch
  clearSearch(): void {
    this.searchTerm = '';
    this.appliedSearch = '';
    this.first = 0;
    this.loadData(1, this.rows);
  }

  /*
   ██████╗  ██████╗     ████████╗ ██████╗     ██╗   ██╗███████╗███████╗██████╗
  ██╔════╝ ██╔═══██╗    ╚══██╔══╝██╔═══██╗    ██║   ██║██╔════╝██╔════╝██╔══██╗
  ██║  ███╗██║   ██║       ██║   ██║   ██║    ██║   ██║███████╗█████╗  ██████╔╝
  ██║   ██║██║   ██║       ██║   ██║   ██║    ██║   ██║╚════██║██╔══╝  ██╔══██╗
  ╚██████╔╝╚██████╔╝       ██║   ╚██████╔╝    ╚██████╔╝███████║███████╗██║  ██║
   ╚═════╝  ╚═════╝        ╚═╝    ╚═════╝      ╚═════╝ ╚══════╝╚══════╝╚═╝  ╚═╝

  */
  // region goToUser
  goToUser(
    user: AppUser,
    event?: Event
  ): void {

    /* Solo llega evento desde los atajos de teclado de la fila. El clic del
       mouse no lo manda porque cualquier celda es un origen válido. */
    if (event) {

      /* Enter y Espacio disparados sobre el enlace del nombre burbujean hasta el
         <tr>: si el evento no nació en la fila misma, la acción le pertenece al
         enlace y la fila no debe navegar otra vez. */
      if (event.target !== event.currentTarget) {
        return;
      }

      /* Evita que la barra espaciadora haga scroll de la página al activar. */
      event.preventDefault();

    }

    /* Ruta relativa: UsersPageComponent vive en el path '' de `users`, así que
       el uid se cuelga de `dashboard/security/users/`. */
    this.router.navigate(
      [user.uid],
      { relativeTo: this.activatedRoute }
    );

  }

  /*
  ██╗      ██████╗  █████╗ ██████╗     ██████╗  █████╗ ████████╗ █████╗
  ██║     ██╔═══██╗██╔══██╗██╔══██╗    ██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
  ██║     ██║   ██║███████║██║  ██║    ██║  ██║███████║   ██║   ███████║
  ██║     ██║   ██║██╔══██║██║  ██║    ██║  ██║██╔══██║   ██║   ██╔══██║
  ███████╗╚██████╔╝██║  ██║██████╔╝    ██████╔╝██║  ██║   ██║   ██║  ██║
  ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

  */
  // region loadData
  async loadData(
    page: number,
    pageSize: number
  ): Promise<void> {

    this.liveMessage = 'Cargando usuarios';

    await this.appUserService.getAll(
      this.appliedSearch,
      page,
      pageSize
    );

    const total: number = this.appUserService.total;

    if (total === 0) {
      this.liveMessage = this.appliedSearch
        ? `Sin resultados para ${this.appliedSearch}`
        : 'No hay usuarios registrados';
    } else {
      this.liveMessage = total === 1
        ? '1 usuario encontrado'
        : `${total} usuarios encontrados`;
    }

  }

  /*
   ██████╗ ███╗   ██╗██╗      █████╗ ███████╗██╗   ██╗██╗      ██████╗  █████╗ ██████╗
  ██╔═══██╗████╗  ██║██║     ██╔══██╗╚════██║╚██╗ ██╔╝██║     ██╔═══██╗██╔══██╗██╔══██╗
  ██║   ██║██╔██╗ ██║██║     ███████║    ██╔╝ ╚████╔╝ ██║     ██║   ██║███████║██║  ██║
  ██║   ██║██║╚██╗██║██║     ██╔══██║   ██╔╝   ╚██╔╝  ██║     ██║   ██║██╔══██║██║  ██║
  ╚██████╔╝██║ ╚████║███████╗██║  ██║   ██║     ██║   ███████╗╚██████╔╝██║  ██║██████╔╝
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚═╝  ╚═╝   ╚═╝     ╚═╝   ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝

  */
  // region onLazyLoad
  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 25;
    const page = Math.floor(this.first / this.rows) + 1;
    this.loadData(page, this.rows);
  }

  /*
  ███████╗███████╗ █████╗ ██████╗  ██████╗██╗  ██╗
  ██╔════╝██╔════╝██╔══██╗██╔══██╗██╔════╝██║  ██║
  ███████╗█████╗  ███████║██████╔╝██║     ███████║
  ╚════██║██╔══╝  ██╔══██║██╔══██╗██║     ██╔══██║
  ███████║███████╗██║  ██║██║  ██║╚██████╗██║  ██║
  ╚══════╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝

  */
  // region search
  search(): void {
    this.appliedSearch = this.searchTerm.trim();
    this.first = 0;
    this.loadData(1, this.rows);
  }

}
